import React, { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { ShieldAlert, ArrowRight, Loader2 } from "lucide-react"
import { fetchExpiringDocuments } from "../api/documentsApi"
import { DocumentIcon } from "./DocumentIcon"
import { categoryLabel, EXPIRY_BADGE } from "../constants"

function relativeExpiry(iso) {
  if (!iso) return ""
  const days = Math.round((new Date(iso) - Date.now()) / 86_400_000)
  if (days < 0) return `${Math.abs(days)}d overdue`
  if (days === 0) return "today"
  return `in ${days}d`
}

// Dashboard compliance widget: documents that have expired or expire within 30
// days. Renders nothing while empty so it doesn't clutter a clean dashboard.
export function ExpiringDocumentsWidget() {
  const [docs, setDocs] = useState(null)

  useEffect(() => {
    fetchExpiringDocuments().then(setDocs).catch(() => setDocs([]))
  }, [])

  if (docs && docs.length === 0) return null

  return (
    <div className="col-span-1 rounded-xl border border-amber-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-amber-600" />
          <span className="text-sm font-semibold text-slate-800">Document Compliance</span>
        </div>
        <Link to="/documents" className="flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:text-blue-700">
          All docs <ArrowRight className="h-3 w-3" />
        </Link>
      </div>

      {docs === null ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-4 w-4 animate-spin text-slate-300" />
        </div>
      ) : (
        <ul className="space-y-2">
          {docs.slice(0, 5).map((doc) => {
            const badge = EXPIRY_BADGE[doc.expiry_status]
            return (
              <li key={doc.id} className="flex items-center gap-2.5">
                <DocumentIcon contentType={doc.content_type} fileName={doc.file_name} size="sm" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13px] font-medium text-slate-700">{doc.title || doc.file_name}</div>
                  <div className="truncate text-[11px] text-slate-400">
                    {categoryLabel(doc.category)}
                    {doc.documentable?.label ? ` · ${doc.documentable.label}` : ""}
                  </div>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ${badge?.className || ""}`}>
                  {relativeExpiry(doc.expires_at)}
                </span>
              </li>
            )
          })}
          {docs.length > 5 && (
            <li className="pt-1 text-center text-[11px] text-slate-400">+{docs.length - 5} more</li>
          )}
        </ul>
      )}
    </div>
  )
}
